import { ReactNode } from 'react';
import { ToastsToIgnoreSettings } from 'src/environmentVariables/EnvironmentVariables';
import useNotificationStore from '../stores/useNotificationStore';
import { MarketOrderToastState } from '../components/MarketOrderToasts/MarketOrderToastStateTypes';
import { dlog } from '../dev';
import { VelocityAppEventEmitter } from 'src/stores/VelocityStore/useVelocityStore';
import { GLOBAL_SETTINGS_CLIENT } from 'src/providers/settingsProvider';
import { SOLANA_TRANSACTION_BLOCK_HEIGHT_EXPIRY } from '../constants/constants';

type CoreToastData = {
	id?: string;
	hidden?: boolean;
	/**
	 * If true, will not show another toast if this is already showing.
	 * Useful if meant to be the last toast in a sequence.
	 */
	blockSubsequentToasts?: boolean;
	/**
	 * If true, will prevent the toast from being cleared by `useClearStaleNotifications`.
	 */
	isHovered?: boolean;
	/**
	 * Tracks which process last updated this notification.
	 * Used for debugging and understanding data flow.
	 */
	source?: string;
};

export interface V2AuctionToastDataBody
	extends MarketOrderToastState, CoreToastData {}

export interface MarketOrderToast extends V2AuctionToastDataBody {
	type: 'marketOrder';
}

export type CallbackAction = {
	label: string;
	callback: () => void;
	type?:
		| 'default' // label with arrow pointing right
		| 'button';
};

export type TxnLinkAction = {
	txnSig: string | string[];
	type: 'txnLink';
};

export type ExternalLinkAction = {
	label?: string;
	href: string;
	type: 'link';
};

export type CustomAction = {
	type: 'custom';
	content: ReactNode;
};

export type NotificationAction =
	| CallbackAction
	| TxnLinkAction
	| ExternalLinkAction
	| CustomAction;

// NOTE: If you add a new notification type but want it to have it's own clearing logic, make sure to adjust the list of types in useClearStaleNotifications
export type NotificationType =
	| 'error'
	| 'warning'
	| 'info'
	| 'success'
	| 'awaiting'
	| 'alert'
	| 'timeout'
	| 'awaiting_wallet_confirmation'
	| 'progress_indicator';

/**
 * Fields common to both a `notify()` input and a stored notification,
 * regardless of `type`. Both `NotificationData` (the stored shape) and every
 * member of the `NotificationProps` input union are composed from this and the
 * variant-specific field groups below, so the input and stored shapes can't
 * drift apart.
 */
interface CommonNotificationFields extends CoreToastData {
	bgType?: 'error' | 'info' | 'success';
	message?: ReactNode;
	description?: ReactNode;
	subDescription?: ReactNode;
	/**
	 * Allows for more than just `description` and `subDescription`.
	 */
	steps?: ReactNode[];
	action?: NotificationAction;
	offerIgnoreToast?: keyof ToastsToIgnoreSettings;
	showUntilCancelled?: boolean;
	statusWarning?: string;
	bottomContent?: ReactNode;
	/**
	 * If `true`, will override a blocking toast.
	 */
	overrideBlockSubsequentToasts?: boolean;
	/**
	 * If `true`, shows a timeout notification if a transaction is taking too long and `TransactionErrorHandler` failed to catch it.
	 */
	timeoutIfProlongedTxn?: boolean;
	/**
	 * Callback to be invoked when the notification is dismissed.
	 */
	onDismiss?: () => void;
	/**
	 * If true, shows a white X button that works well on dark backgrounds.
	 * Useful for notifications with custom background images or dark themes.
	 */
	whiteXButton?: boolean;
}

/**
 * Fields that only apply to `alert` notifications.
 */
interface AlertFields {
	/**
	 *  Determines styling and behavior of "alert" notifications.
	 */
	alertType?: 'minor' | 'major';
	/**
	 * Only exists for major "alert" notifications.
	 */
	imageSrc?: string;
	/**
	 * If passed, notification will only show for the given path.
	 */
	pathToAlertOn?: string;
	/**
	 * Disables auto-dismiss of "alert" notifications.
	 * Generally should be the exception to the norm.
	 */
	disableAlertAutoDismiss?: boolean;
	/**
	 * Used to correctly order notifications in the stack. If setting this,
	 * be sure to check the `stackIndex` of other notifications as well.
	 */
	stackIndex?: number;
}

/**
 * The timeout-window fields shared by `timeout` and
 * `awaiting_wallet_confirmation` notifications.
 */
interface TimeoutWindowFields {
	timeoutStartMs?: number;
	timeoutEndMs?: number;
	/**
	 * A deadline expressed in blocks rather than wall-clock, for windows that
	 * really are block-denominated (a transaction's blockhash expiry). Blocks
	 * are not slots: skipped slots make the real window longer, so a renderer
	 * converting these at the slot rate finishes its countdown early, which is
	 * the safe direction. The renderer owns the conversion; nothing here may
	 * assume a block time.
	 */
	timeoutBlockCount?: number;
}

/**
 * The stored shape of a notification, once `notify()` has applied its
 * defaults. Deliberately a flat bag of every field any `type` could use: the
 * renderer components read variant-specific fields (e.g. `alertType`,
 * `timeoutStartMs`) off this without first narrowing on `type`. Composed from
 * the same field groups as the `NotificationProps` input union so the stored
 * and input shapes can't drift.
 */
export interface NotificationData
	extends CommonNotificationFields, AlertFields, TimeoutWindowFields {
	type: NotificationType;
	startTimeMs: number;
	lengthMs: number;
}

/**
 * Fields shared by every `notify()` input, regardless of `type`.
 */
interface BaseNotificationProps extends CommonNotificationFields {
	lengthMs?: number;
	startTimeMs?: number;
	updatePrevious?: boolean;
	onlyShowIfPreviousTarget?: boolean;
}

export interface SimpleNotificationProps extends BaseNotificationProps {
	type: Exclude<
		NotificationType,
		'alert' | 'timeout' | 'awaiting_wallet_confirmation'
	>;
}

export interface AlertNotificationProps
	extends BaseNotificationProps, AlertFields {
	type: 'alert';
}

export interface TimeoutNotificationProps
	extends BaseNotificationProps, TimeoutWindowFields {
	type: 'timeout';
}

export interface AwaitingWalletConfirmationProps
	extends BaseNotificationProps, TimeoutWindowFields {
	type: 'awaiting_wallet_confirmation';
	/**
	 * If both `timeoutStartMs` and `timeoutEndMs` are omitted, `notify()` starts
	 * the window now and hands the renderer the Solana block height expiry as a
	 * block count - see `buildAwaitingWalletConfirmationOverrides` below.
	 */
}

/**
 * The input type for `notify()`: a discriminated union keyed on `type`, so
 * fields that only make sense for one notification type (e.g. `alertType` on
 * `alert`, `timeoutStartMs` on `timeout`) are unrepresentable on the others.
 * TypeScript narrows a bare object literal by its `type` discriminant and
 * applies excess-property checking against the matching variant, so
 * `notify({ type: 'error', alertType: 'minor' })` is rejected at the call site.
 */
export type NotificationProps =
	| SimpleNotificationProps
	| AlertNotificationProps
	| TimeoutNotificationProps
	| AwaitingWalletConfirmationProps;

const DEFAULT_PROPS: Partial<BaseNotificationProps> = {
	updatePrevious: true,
	showUntilCancelled: false,
	onlyShowIfPreviousTarget: false,
	description: '', // this way if we updatePrevious with no description, old description will be cleared
};

/**
 * Computes the wallet-confirmation timeout window and copy that get
 * pre-filled for `awaiting_wallet_confirmation` notifications, so callers
 * don't need to repeat this boilerplate at every call site.
 *
 * This is the notify()-time "constructor" for the `awaiting_wallet_confirmation`
 * variant - keeping it as its own function (rather than inline in `notify()`)
 * means the generic add/update/dedupe algorithm in `notify()` never needs to
 * know about any particular notification type's quirks.
 */
function buildAwaitingWalletConfirmationOverrides(
	props: TimeoutWindowFields,
	autoConfirmEnabled: boolean
): TimeoutWindowFields & Pick<NotificationData, 'message'> {
	const hasExplicitTimeout =
		props.timeoutStartMs !== undefined || props.timeoutEndMs !== undefined;

	return {
		timeoutStartMs: hasExplicitTimeout ? props.timeoutStartMs : Date.now(),
		timeoutEndMs: hasExplicitTimeout ? props.timeoutEndMs : undefined,
		timeoutBlockCount: hasExplicitTimeout
			? undefined
			: SOLANA_TRANSACTION_BLOCK_HEIGHT_EXPIRY,
		message: autoConfirmEnabled
			? 'Sending transaction...'
			: 'Confirm the transaction in your wallet',
	};
}

/**
 * Display a notification to the user, in toast format.
 *
 * The optional `id` parameter can be used to overwrite a currently displayed notification (with the same ID) rather than display a whole new notification. You can change any of the current notification's attributes.
 *
 * The showUntilCancelled prop allows you to keep a notification on screen until you overwrite it with a later notification
 *
 * @param {NotificationProps} props
 *
 * @returns
 */
export function notify(props: NotificationProps) {
	const newNotificationProps = {
		...DEFAULT_PROPS,
		...props,
	};

	dlog(`notifications`, `notify`, {
		newNotificationProps,
	});

	const notifications = useNotificationStore.getState().notifications;
	const notificationStore = useNotificationStore.getState();
	const settingsClient = GLOBAL_SETTINGS_CLIENT;

	const shouldIgnoreToast =
		newNotificationProps.offerIgnoreToast &&
		settingsClient.overallSettingsState.ignoreToasts[
			newNotificationProps.offerIgnoreToast
		];

	if (shouldIgnoreToast) return;

	const newNotificationToAdd: NotificationData = {
		startTimeMs: Date.now(),
		...newNotificationProps,
		lengthMs: newNotificationProps.lengthMs ?? 5000,
		showUntilCancelled: newNotificationProps.showUntilCancelled,
	};

	// We let awaiting_wallet_confirmation be a special case where we basically just use the timeout notification type but will pre-fill the notification with standard content to avoid needing to repeat it everywhere
	if (props.type === 'awaiting_wallet_confirmation') {
		const overrides = buildAwaitingWalletConfirmationOverrides(
			props,
			notificationStore.autoConfirmEnabled
		);
		newNotificationToAdd.timeoutStartMs = overrides.timeoutStartMs;
		newNotificationToAdd.timeoutEndMs = overrides.timeoutEndMs;
		newNotificationToAdd.timeoutBlockCount = overrides.timeoutBlockCount;
		newNotificationToAdd.message = overrides.message;
	}

	// Check for notifications with duplicate ID
	//// Unless updating a previous notification via its ID - then we don't want to add a duplicate for the notification
	const targetNotification = notifications.find(
		(notification) =>
			notification.id && notification.id === newNotificationProps.id
	);

	if (targetNotification) {
		// if not updating, but we have an ID clash - skip
		if (!newNotificationProps.updatePrevious) return;

		// if updating, but the previous target is already showing, skip
		if (
			targetNotification.blockSubsequentToasts &&
			!newNotificationProps.overrideBlockSubsequentToasts
		)
			return;
	}

	// if updating previous but no matching target, also skip
	if (newNotificationProps.onlyShowIfPreviousTarget && !targetNotification)
		return;

	if (targetNotification) {
		notificationStore.updateNotificationById(targetNotification.id ?? '', {
			...newNotificationToAdd,
			showUntilCancelled: newNotificationProps.showUntilCancelled,
		});
	} else {
		notificationStore.addNotification(newNotificationToAdd);
	}
}

export const getTimeoutNotificationData = (
	appEventEmitter: VelocityAppEventEmitter
): SimpleNotificationProps => ({
	type: 'warning',
	message: 'Transaction Confirmation Timeout',
	description: `Your transaction wasn't confirmed within 30 seconds, but it may still go through. You can use the Solana explorer to check. If you experience this frequently, you should try changing your Priority Fee Settings.`,
	action: {
		label: 'Priority Fee Settings',
		callback: () =>
			appEventEmitter.emit('showModal', {
				type: 'settings',
				props: { view: 'network' },
			}),
	},
	updatePrevious: true,
	showUntilCancelled: true,
	subDescription: undefined,
});
